/**
 * Custom Quantity Button Handler
 * Handles quantity pack button functionality for product pages.
 * Scoped to a container element so multiple instances (product page,
 * quick view, featured product) can coexist without interfering.
 */

class QuantityButtonHandler {
  constructor(container) {
    this.container = container;
    this.setup();
  }

  setup() {
    const buttons = this.container.querySelectorAll('.quantity-button');
    if (!buttons.length) return;

    // Button → quantity
    buttons.forEach(btn => {
      btn.addEventListener('click', e => {
        e.preventDefault();
        this.activate(btn);
      });
    });

    // Quantity → button: intercept the value property setter so we catch
    // Alpine x-model updates (which do not fire native DOM events).
    const input = this.container.querySelector('input[name="quantity"]');
    if (input) {
      const handler = this;
      const proto = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value');
      Object.defineProperty(input, 'value', {
        configurable: true,
        get() { return proto.get.call(this); },
        set(val) {
          proto.set.call(this, val);
          handler.syncButtonsToQuantity(parseInt(val, 10));
        }
      });
    }

    // On page load, simulate a click on the pre-rendered active button
    // (or the first button) after Alpine has finished its init pass.
    // This single code path handles quantity sync, button state, and
    // avoids any special-casing of Alpine internals.
    const defaultBtn =
      this.container.querySelector('.quantity-button--active[data-pack]') ||
      this.container.querySelector('.quantity-button[data-pack]');

    if (defaultBtn) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          defaultBtn.click();
        });
      });
    }
  }

  activate(btn) {
    const pack = parseInt(btn.getAttribute('data-pack'));
    if (isNaN(pack)) return;

    // Update quantity stepper via Alpine reactive data if available,
    // otherwise fall back to direct value + event dispatch.
    const input = this.container.querySelector('input[name="quantity"]');
    if (input) {
      const alpineData = window.Alpine?.$data(input);
      if (alpineData && 'qty' in alpineData) {
        alpineData.qty = pack;
      } else {
        input.value = pack;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        input.dispatchEvent(new Event('input', { bubbles: true }));
      }
    }

    this.syncButtonsToQuantity(pack);
  }

  syncButtonsToQuantity(quantity) {
    if (isNaN(quantity)) return;
    this.container.querySelectorAll('.quantity-button').forEach(btn => {
      const pack = parseInt(btn.getAttribute('data-pack'));
      const active = pack === quantity;
      btn.classList.toggle('quantity-button--active', active);
      btn.setAttribute('aria-pressed', String(active));
    });
  }
}

// One handler per quantity selector container — safe for quick view + product page coexisting.
// Guard with _quantityHandler to prevent double-init.
function initQuantityButtonHandlers() {
  document.querySelectorAll('.product-form__quantity').forEach(container => {
    if (container.querySelector('.quantity-button') && !container._quantityHandler) {
      container._quantityHandler = new QuantityButtonHandler(container);
    }
  });
}


if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initQuantityButtonHandlers);
} else {
  initQuantityButtonHandlers();
}

if (typeof module !== 'undefined' && module.exports) {
  module.exports = QuantityButtonHandler;
}


